import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { crmService } from '../services';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  User, Mail, Building, Shield, Phone, MapPin, Globe, 
  Loader2, AlertCircle, Cloud, RefreshCw, Calendar 
} from 'lucide-react';
import { toast } from 'sonner';

export const ProfilePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [error, setError] = useState(null);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await crmService.getProfile();
      setProfileData(data);
      if (data.error) {
        setError(data.error);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Fehler beim Laden der Stammdaten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const contact = profileData?.contact;
  const account = profileData?.account;
  const source = profileData?.source;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="profile-page"
    >
      {/* Page Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
            Stammdaten
          </h1>
          <p className="mt-2" style={{ color: 'var(--color-text-secondary)' }}>
            Ihre hinterlegten Kontakt- und Firmendaten
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchProfile}
          disabled={loading}
          data-testid="refresh-profile"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Data Source Info */}
      {source && (
        <div className={`mb-6 p-4 rounded-lg border flex items-center gap-3 ${
          (source === 'zoho_crm' || source === 'zoho_books')
            ? 'bg-green-50 border-green-200' 
            : source === 'no_credentials' || source === 'error'
            ? 'bg-yellow-50 border-yellow-200'
            : 'bg-blue-50 border-blue-200'
        }`}>
          {(source === 'zoho_crm' || source === 'zoho_books') ? (
            <>
              <Cloud className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">
                  Live-Daten von {source === 'zoho_books' ? 'Zoho Books' : 'Zoho CRM'}
                </p>
                <p className="text-xs text-green-600">
                  {profileData?.found 
                    ? 'Kontakt gefunden' 
                    : 'Kein Kontakt mit Ihrer E-Mail gefunden'
                  }
                </p>
              </div>
            </>
          ) : (
            <>
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  {error || 'Zoho CRM nicht verbunden'}
                </p>
                <p className="text-xs text-yellow-600">
                  Bitte konfigurieren Sie die Zoho CRM Verbindung im Admin-Bereich
                </p>
              </div>
            </>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Profile Card */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-xl font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Profilübersicht
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center text-center space-y-4">
              <Avatar className="w-24 h-24">
                <AvatarFallback className="text-2xl bg-blue-100 text-blue-700">
                  {contact?.first_name?.charAt(0) || user?.email?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {contact?.full_name || user?.email}
                </h3>
                {contact?.account_name && (
                  <p className="text-sm text-slate-500 mt-1">{contact.account_name}</p>
                )}
              </div>
              <div className="w-full pt-4 border-t border-slate-100 space-y-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{contact?.email || user?.email}</span>
                </div>
                {contact?.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    {contact.phone}
                  </div>
                )}
                {contact?.mobile && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    {contact.mobile} (Mobil)
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Shield className="w-4 h-4 flex-shrink-0" />
                  Rolle: {user?.role}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="lg:col-span-2 bg-white border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Kontaktdaten
                </CardTitle>
                <CardDescription>
                  Ihre in Zoho CRM hinterlegten Kontaktinformationen
                </CardDescription>
              </div>
              {source === 'zoho_crm' && profileData?.found && (
                <Badge className="bg-green-100 text-green-800 border border-green-200">
                  CRM verknüpft
                </Badge>
              )}
              {source === 'zoho_books' && profileData?.found && (
                <Badge className="bg-green-100 text-green-800 border border-green-200">
                  Books verknüpft
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {profileData?.found && contact ? (
              <div className="space-y-6">
                {/* Customer Number */}
                {contact.contact_number && (
                  <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Kundennummer</label>
                    <p className="mt-1 text-lg font-semibold text-slate-900" data-testid="customer-number">{contact.contact_number}</p>
                  </div>
                )}

                {/* Name Section */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Vorname</label>
                    <p className="mt-1 text-slate-900">{contact.first_name || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Nachname</label>
                    <p className="mt-1 text-slate-900">{contact.last_name || '-'}</p>
                  </div>
                </div>

                {/* Contact Section */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">E-Mail</label>
                    <p className="mt-1 text-slate-900">{contact.email || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Telefon</label>
                    <p className="mt-1 text-slate-900">{contact.phone || contact.mobile || '-'}</p>
                  </div>
                </div>

                {/* Address Section */}
                {(contact.mailing_street || contact.mailing_city) && (
                  <div className="pt-4 border-t border-slate-100">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      Adresse
                    </label>
                    <div className="mt-2 text-slate-900">
                      {contact.mailing_street && <p>{contact.mailing_street}</p>}
                      {(contact.mailing_zip || contact.mailing_city) && (
                        <p>{[contact.mailing_zip, contact.mailing_city].filter(Boolean).join(' ')}</p>
                      )}
                      {contact.mailing_country && <p>{contact.mailing_country}</p>}
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div className="pt-4 border-t border-slate-100 grid md:grid-cols-2 gap-4 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Erstellt: {contact.created_at ? new Date(contact.created_at).toLocaleDateString('de-DE') : '-'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Aktualisiert: {contact.updated_at ? new Date(contact.updated_at).toLocaleDateString('de-DE') : '-'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <User className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">
                  Kein Kontakt gefunden
                </h3>
                <p className="text-sm text-slate-500">
                  Es wurde kein Kontakt mit der E-Mail-Adresse <strong>{profileData?.user_email}</strong> in Zoho CRM gefunden.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Account/Company Information */}
        {account && (
          <Card className="lg:col-span-3 bg-white border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-600" />
                <CardTitle className="text-xl font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Firmendaten
                </CardTitle>
              </div>
              <CardDescription>
                Ihr zugeordnetes Unternehmen in Zoho CRM
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Firmenname</label>
                  <p className="mt-1 text-slate-900 font-medium">{account.name || '-'}</p>
                </div>
                {account.website && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      Website
                    </label>
                    <p className="mt-1">
                      <a href={account.website.startsWith('http') ? account.website : `https://${account.website}`} 
                         target="_blank" 
                         rel="noopener noreferrer"
                         className="text-blue-600 hover:underline">
                        {account.website}
                      </a>
                    </p>
                  </div>
                )}
                {account.phone && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Telefon</label>
                    <p className="mt-1 text-slate-900">{account.phone}</p>
                  </div>
                )}
                {account.industry && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Branche</label>
                    <p className="mt-1 text-slate-900">{account.industry}</p>
                  </div>
                )}
                {(account.billing_street || account.billing_city) && (
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      Rechnungsadresse
                    </label>
                    <div className="mt-1 text-slate-900">
                      {account.billing_street && <p>{account.billing_street}</p>}
                      {(account.billing_zip || account.billing_city) && (
                        <p>{[account.billing_zip, account.billing_city].filter(Boolean).join(' ')}</p>
                      )}
                      {account.billing_country && <p>{account.billing_country}</p>}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </motion.div>
  );
};
